import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

const inputCls =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition';

export default function AccountPickerModal({ title, accounts, selectedIds, onCancel, onConfirm }) {
  const [searchText, setSearchText] = useState('');
  const [checked, setChecked] = useState(() => new Set(selectedIds));
  const visible = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return accounts;
    return accounts.filter(
      account =>
        account.account_name?.toLowerCase().includes(query) ||
        account.account_type_name?.toLowerCase().includes(query) ||
        account.parent_account_name?.toLowerCase().includes(query)
    );
  }, [accounts, searchText]);
  const toggle = accountId => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(accountId)) next.delete(accountId);
    else next.add(accountId);
    return next;
  });
  const allVisibleChecked = visible.length > 0 && visible.every(account => checked.has(account.account_id));
  const toggleAllVisible = () => setChecked(prev => {
    const next = new Set(prev);
    visible.forEach(account => {
      if (allVisibleChecked) next.delete(account.account_id);
      else next.add(account.account_id);
    });
    return next;
  });

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        <button type="button" onClick={onCancel} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
      </div>
      <div className="border-b border-gray-100 px-5 py-3"><div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input autoFocus value={searchText} onChange={event => setSearchText(event.target.value)} placeholder="Search accounts…" className={`${inputCls} pl-9`} type="text" />
      </div></div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {visible.length > 0 ? <>
          <label className="flex cursor-pointer items-center gap-3 border-b border-gray-100 bg-gray-50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <input type="checkbox" checked={allVisibleChecked} onChange={toggleAllVisible} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            Select all ({visible.length})
          </label>
          {visible.map(account => <label key={account.account_id} className="flex cursor-pointer items-center gap-3 border-b border-gray-50 px-5 py-2.5 hover:bg-gray-50">
            <input type="checkbox" checked={checked.has(account.account_id)} onChange={() => toggle(account.account_id)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="min-w-0"><span className="block truncate text-sm font-medium text-gray-800">{account.account_name}</span><span className="block truncate text-xs text-gray-400">{account.account_type_name}{account.parent_account_name ? ` · under ${account.parent_account_name}` : ''}</span></span>
          </label>)}
        </> : <p className="px-5 py-10 text-center text-sm text-gray-500">No accounts found.</p>}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
        <span className="text-sm text-gray-500">{checked.size} selected</span>
        <div className="flex gap-3"><button type="button" onClick={onCancel} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">Cancel</button><button type="button" onClick={() => onConfirm([...checked])} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Done</button></div>
      </div>
    </div>
  </div>;
}
